import * as Sentry from "@sentry/react";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import "./styles/fonts.css";
import "./index.css";
import { ThemeProvider } from "./components/theme-provider";
import { SkinProvider } from "./components/skin-provider";
import { getBrowserSentryConfig } from "./build/runtimeSentryConfig";
import { installTranslationDomGuard } from "./build/translationDomGuard";
import { LanguageProvider } from "./i18n/language-provider";
import { AppUnavailable } from "./components/AppUnavailable";
import { RenderFaultBoundary } from "./components/RenderFaultBoundary";
import { CookieNotice } from "./components/legal/CookieNotice";
import { navigate } from "./navigation/history";
import { routePattern } from "./navigation/routes";

/**
 * Session Replay is deliberately absent.
 *
 * `replayIntegration()` records the DOM of the authenticated app, which is
 * full of one hotel's commercial data — its rates, its occupancy, its
 * booking pace — and would carry it to a processor that the privacy notice
 * names for technical error reports only. No screen renders a guest's name
 * any more, and that is not what makes replay safe: masking is not a defence
 * here, because the operator is a processor for guest data on the hotel's
 * behalf (Art. 28) and replay is not a purpose any of them agreed to.
 * Tracing stays: it samples transaction timings, not content.
 *
 * Turning replay back on means a lawful basis, a line in the "Zpracovatelé"
 * section of `legalContent.ts`, and an entry in the storage inventory that
 * `CookieNotice` discloses.
 */
Sentry.init({
  ...getBrowserSentryConfig(import.meta.env),
  integrations: [
    Sentry.browserTracingIntegration({
      // Every page has a URL now, and a strategy's URL carries its id: named
      // by the path, each strategy would become a transaction of its own.
      // Named by the pattern, they group as the one screen they are.
      beforeStartSpan: (context) => ({
        ...context,
        name: routePattern(window.location.pathname),
        attributes: { ...context.attributes, "sentry.source": "route" },
      }),
    }),
  ],
  tracesSampleRate: 0.2,
});

// Before the first render: an extension that translates the page despite
// translate="no" must not crash React on its next update (see
// translationDomGuard.ts).
installTranslationDomGuard();

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    {/*
      The backstop, under every other boundary: a render fault nothing below
      catches (the shell itself, a provider) used to unmount everything and
      leave a blank window. It stands above the providers because one that
      fails takes all it wraps along, so what it shows reads none of them: the
      fallback finds its own language, and is told to follow the browser as
      the provider below is. It offers a reload and takes none, and tries
      nothing again; see `AppUnavailable` for how that differs from
      `staleChunkRecovery`, which reloads by itself for a route's missing chunk.
    */}
    <RenderFaultBoundary part="app" fallback={<AppUnavailable followBrowserLanguage />}>
      <ThemeProvider defaultTheme="system" storageKey="optivo-ui-theme">
        <SkinProvider>
          <LanguageProvider defaultLanguage="en" followBrowserLanguage storageKey="optivo-ui-language">
            <App />
            {/*
              Mounted beside App rather than inside it: App returns early for the
              legal documents, the pending-approval screen and the deactivated
              screen, and the disclosure has to appear on all of them. One mount
              here covers every surface without touching that branching.
            */}
            <CookieNotice onNavigate={navigate} />
          </LanguageProvider>
        </SkinProvider>
      </ThemeProvider>
    </RenderFaultBoundary>
  </StrictMode>,
);
